import React, { useState, useMemo } from "react";
import useSWR from "swr";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, ShieldCheck, ChevronLeft, ChevronRight, LogIn, Plus, Pencil, Trash2 } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const ACTION_TONE = {
  login: "bg-info/15 text-info",
  create: "bg-success/15 text-success",
  update: "bg-warning/15 text-warning",
  delete: "bg-destructive/15 text-destructive",
};

const ACTION_ICON = {
  login: LogIn,
  create: Plus,
  update: Pencil,
  delete: Trash2,
};

const ENTITY_LABEL = {
  units: "Unit", tenants: "Penyewa", contracts: "Kontrak",
  bills: "Tagihan", payments: "Pembayaran", expenses: "Biaya",
  handovers: "Serah Terima", users: "Pengguna", auth: "Auth",
};

const PAGE_SIZE = 50;

function formatTimestamp(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("id-ID", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

export default function AuditLogs() {
  const { user: current } = useAuth();
  const canView = current?.role === "owner";

  const [filters, setFilters] = useState({ entity_type: "all", action: "all", date_from: "", date_to: "" });
  const [offset, setOffset] = useState(0);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.entity_type !== "all") p.set("entity_type", filters.entity_type);
    if (filters.action !== "all") p.set("action", filters.action);
    if (filters.date_from) p.set("date_from", `${filters.date_from}T00:00:00Z`);
    if (filters.date_to) p.set("date_to", `${filters.date_to}T23:59:59Z`);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(offset));
    return p.toString();
  }, [filters, offset]);

  const { data, isLoading } = useSWR(canView ? `/audit-logs?${qs}` : null, fetcher);

  if (!canView) {
    return (
      <div className="max-w-md mx-auto text-center py-16 space-y-3">
        <ShieldCheck className="h-10 w-10 mx-auto text-muted-foreground" />
        <h2 className="font-display text-2xl font-medium">Akses terbatas</h2>
        <p className="text-sm text-muted-foreground">Hanya pemilik yang dapat melihat audit log.</p>
      </div>
    );
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applyFilter = (patch) => { setFilters({ ...filters, ...patch }); setOffset(0); };
  const resetFilter = () => { setFilters({ entity_type: "all", action: "all", date_from: "", date_to: "" }); setOffset(0); };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Keamanan & Jejak Aktivitas</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Rekaman aksi mutasi (create / update / delete) dan login oleh seluruh pengguna. Total {total.toLocaleString("id-ID")} kejadian.
        </p>
      </header>

      <Card>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Entitas</Label>
            <Select value={filters.entity_type} onValueChange={(v) => applyFilter({ entity_type: v })}>
              <SelectTrigger data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                {Object.entries(ENTITY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Aksi</Label>
            <Select value={filters.action} onValueChange={(v) => applyFilter({ action: v })}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Dari</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => applyFilter({ date_from: e.target.value })} data-testid="audit-filter-from" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sampai</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => applyFilter({ date_to: e.target.value })} data-testid="audit-filter-to" />
          </div>
          <Button variant="outline" onClick={resetFilter} className="w-full" data-testid="audit-filter-reset">Reset</Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <ScrollText className="h-7 w-7" />
            </div>
            <div className="font-display text-lg font-medium">Tidak ada log</div>
            <p className="text-sm text-muted-foreground -mt-2">Coba longgarkan filter atau tunggu aktivitas baru.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="hidden md:grid md:grid-cols-12 gap-3 px-4 py-2.5 bg-secondary/50 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            <div className="col-span-3">Waktu</div>
            <div className="col-span-3">Pengguna</div>
            <div className="col-span-2">Aksi</div>
            <div className="col-span-2">Entitas</div>
            <div className="col-span-2">Detail</div>
          </div>
          <div className="divide-y divide-border/50">
            {items.map((log) => {
              const Icon = ACTION_ICON[log.action] || ScrollText;
              return (
                <div key={log.id} className="px-4 py-3 md:grid md:grid-cols-12 md:gap-3 md:items-center flex flex-col gap-2" data-testid={`audit-row-${log.id}`}>
                  <div className="md:col-span-3 text-xs font-mono text-muted-foreground tabular-nums">
                    {formatTimestamp(log.timestamp)}
                  </div>
                  <div className="md:col-span-3 flex items-center gap-2 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-medium shrink-0">
                      {log.user_email?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm truncate">{log.user_email || "system"}</div>
                      {log.user_role && (
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{log.user_role}</div>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Badge className={`rounded-full text-[10px] uppercase tracking-wider gap-1 ${ACTION_TONE[log.action] || "bg-secondary"}`}>
                      <Icon className="h-3 w-3" /> {log.action}
                    </Badge>
                  </div>
                  <div className="md:col-span-2 text-sm">
                    {ENTITY_LABEL[log.entity_type] || log.entity_type}
                  </div>
                  <div className="md:col-span-2 text-xs text-muted-foreground truncate font-mono">
                    {log.entity_id ? `#${log.entity_id.slice(0, 8)}` : (log.path?.replace(/^\/api/, "") || "-")}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Halaman {page} dari {pageCount}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} data-testid="audit-prev">
              <ChevronLeft className="h-4 w-4" /> Sebelumnya
            </Button>
            <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} data-testid="audit-next">
              Berikutnya <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
